import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Car, LayoutDashboard, Calendar, History, BarChart3, User, LogOut, Users, Menu, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { API, authFetch } from '../utils/api';

export default function Layout({ children, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isAdmin = user?.role === 'administrator' || user?.role === 'admin';

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['all'] },
    { name: 'Add Wash', href: '/history', icon: History, roles: ['all'] },
    { name: 'Fleet', href: '/fleet', icon: Car, roles: ['admin'] },
    { name: 'Schedule', href: '/schedule', icon: Calendar, roles: ['all'] },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['admin'] },
    { name: 'Employees', href: '/employees', icon: Users, roles: ['admin'] },
    { name: 'Customers', href: '/customers', icon: UserPlus, roles: ['admin'] },
  ];

  const navigation = allNavigation.filter(item => {
    if (item.roles.includes('all')) return true;
    if (item.roles.includes('admin') && isAdmin) return true;
    return false;
  });

  const handleLogout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: 'POST',
      });
      localStorage.removeItem('session_token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleNavClick = (href) => {
    setMobileMenuOpen(false);
    navigate(href);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Header - with safe area for notch */}
      <div className="lg:hidden bg-slate-900 text-white px-4 pt-12 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Car className="h-6 w-6 text-sky-400" strokeWidth={1.5} />
          <h1 className="text-lg font-heading font-bold tracking-tight">Happy Day Wash</h1>
        </div>
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-3 rounded-md hover:bg-slate-800"
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-slate-900 text-white pt-24">
          <nav className="p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-4 rounded-sm font-heading font-semibold uppercase text-sm tracking-wide transition-all duration-200 w-full text-left ${
                    isActive
                      ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                  {item.name}
                </button>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 p-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-sky-500 text-white font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <Button 
                variant="outline" 
                className="flex-1 border-slate-700 text-white hover:bg-slate-800"
                onClick={() => { setMobileMenuOpen(false); navigate('/profile'); }}
              >
                <User className="h-4 w-4 mr-2" />
                Profile
              </Button>
              <Button 
                variant="outline" 
                className="flex-1 border-slate-700 text-white hover:bg-slate-800"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 bg-slate-900 text-white flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <Car className="h-8 w-8 text-sky-400" strokeWidth={1.5} />
            <h1 className="text-2xl font-heading font-bold tracking-tight">Happy Day Wash</h1>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm font-heading font-semibold uppercase text-sm tracking-wide transition-all duration-200 ${
                  isActive
                    ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-3 w-full p-3 rounded-sm hover:bg-slate-800 transition-colors">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-sky-500 text-white font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-white">{user?.name}</p>
                  <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
